import React, { useState, useRef } from "react";
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { login, submitMfaCode } from "../services/authService";
import { completeLogin } from "../services/postLogin";
import MfaCodeStep from "./MfaCodeStep";

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Null means the password form is showing. Holding the whole challenge in one
  // object keeps "are we mid-challenge" and "what is the challenge" from ever
  // disagreeing.
  const [challenge, setChallenge] = useState(null);
  // Which challenge a code reply belongs to. Abandoning one bumps it, so a
  // verification that was already in flight cannot finish afterwards: the code
  // step offers Start over while a request is out once the window has expired,
  // and without this a correct code landing a moment later would store the
  // session and navigate to /admin out from under the password form the user
  // went back to.
  const challengeGeneration = useRef(0);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Both ways of arriving at a session end here: store it, check the user may
   * use this client, and either go on to /admin or say why not.
   */
  const finishLogin = async (session) => {
    const result = await completeLogin(session);
    if (result.ok) {
      navigate("/admin");
      return;
    }

    // The tokens have been discarded and the challenge, if there was one, is
    // spent — so there is nothing left for the code step to submit. This is the
    // one case where leaving it is right.
    setChallenge(null);
    setError(result.message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await login(formData);

      if (result.status === "success") {
        await finishLogin(result.session);
      } else if (result.status === "mfa_required") {
        setChallenge({
          mfaToken: result.mfaToken,
          factors: result.factors,
          factorId: result.factorId,
          expiresAt: result.expiresAt,
        });
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Answer the challenge. Returns rather than setting page-level state, so a
   * rejected code stays inside the code step instead of throwing the user back
   * to the password form.
   */
  const handleMfaSubmit = async (code) => {
    const issued = challengeGeneration.current;
    const result = await submitMfaCode({
      mfaToken: challenge.mfaToken,
      factorId: challenge.factorId,
      code,
    });

    if (issued !== challengeGeneration.current) {
      // The user left this challenge while the request was out. The code step
      // that asked is already unmounted, so there is nobody to tell — and
      // reporting a rejection would be no more true than acting on a success.
      return { ok: true };
    }

    if (result.status !== "success") {
      return { ok: false, message: result.message };
    }

    await finishLogin(result.session);
    return { ok: true };
  };

  /**
   * Back to the password form. The credentials are left in place so this is one
   * click plus one Sign In, and it deliberately does not re-submit by itself —
   * an implicit request would spend rate-limit budget the user did not ask to
   * spend, against a 429 that carries no Retry-After.
   */
  const handleStartOver = () => {
    challengeGeneration.current += 1;
    setChallenge(null);
    setError("");
  };

  const handleRegister = () => {
    navigate("/register");
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box
            component="img"
            sx={{
              height: 80,
              width: 80,
              mb: 2,
            }}
            alt="FiStone Logo"
            src="/fistone-logo.png"
          />

          <Typography
            component="h1"
            variant="h4"
            sx={{ mb: 3, fontWeight: 600 }}
          >
            {challenge ? "Two-step verification" : "Welcome to FiStone"}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
              {error}
            </Alert>
          )}

          {challenge ? (
            <MfaCodeStep
              factors={challenge.factors}
              factorId={challenge.factorId}
              onFactorChange={(factorId) =>
                setChallenge((current) => ({ ...current, factorId }))
              }
              expiresAt={challenge.expiresAt}
              onSubmit={handleMfaSubmit}
              onStartOver={handleStartOver}
            />
          ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ mt: 1, width: "100%" }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Email Address"
              name="username"
              autoComplete="email"
              autoFocus
              value={formData.username}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              // Fixed rather than derived from the content, which is replaced by
              // a spinner while the request is in flight — without this the
              // button loses its accessible name at exactly the moment a screen
              // reader user is waiting to hear what it is doing.
              aria-label="Sign In"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={loading || !formData.username || !formData.password}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign In"
              )}
            </Button>

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={handleForgotPassword}
                type="button"
                sx={{ textDecoration: "none" }}
              >
                Forgot password?
              </Link>

              <Link
                component="button"
                variant="body2"
                onClick={handleRegister}
                type="button"
                sx={{ textDecoration: "none" }}
              >
                Don't have an account? Sign Up
              </Link>
            </Box>
          </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
}

export default Login;
